import * as z from 'zod'

/**
 * OAuth login-state codec. Isomorphic and dependency-light so it can live in
 * `@tunasong/schemas` and be shared by the browser (which encodes the state
 * before redirecting to Cognito) and the auth-proxy Lambda (which decodes it to
 * validate the originating host) — without either importing the frontend store.
 */
export const loginStateSchema = z.object({
  callbackUrl: z.string(),
  // A random code string the client has stored that will be used to verify the login request
  clientCode: z.string(),
})

export type LoginStateSchema = z.infer<typeof loginStateSchema>

export const decodeLoginState = (state: string) => {
  try {
    // atob (not Buffer) keeps this isomorphic and free of @types/node, so it can
    // live in the dependency-light schemas package. Symmetric with btoa below.
    const str = atob(state)
    const result = loginStateSchema.safeParse(JSON.parse(str))
    return result.success ? result.data : null
  } catch {
    /**
     * The state arrives from a public query parameter — malformed base64 or
     * JSON is an invalid state (null → 403 at the caller), never an exception.
     */
    return null
  }
}

export const encodeLoginState = (state: LoginStateSchema) => {
  const clientStateJson = JSON.stringify(state)
  return btoa(clientStateJson)
}
